import {ChangeDetectionStrategy, Component, inject, input, output, signal} from '@angular/core';
import {NavigationEnd, Router, RouterLink, RouterLinkActive} from '@angular/router';
import {toSignal} from '@angular/core/rxjs-interop';
import {filter, map, startWith} from 'rxjs';
import Keycloak from 'keycloak-js';
import {MatListModule} from '@angular/material/list';
import {MatIconModule} from '@angular/material/icon';
import {NavGroup, NavItem} from '../../navigation/nav.model';

import {hasPermission, PERMISSIONS} from '../../auth/permissions';
import {MatIconButton} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {SIDEBAR_NAV} from './core/sidebar.nav';
import {NgTemplateOutlet} from '@angular/common';
import {Auth} from '../../auth/auth';
import {ExchangeRateAlertService} from '../../services/exchange-rate-alert.service';

@Component({
  selector: 'app-sidebar',
  imports: [RouterLink, RouterLinkActive, MatListModule, MatIconModule, MatIconButton, MatTooltipModule, NgTemplateOutlet],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Sidebar {
  public readonly isMobile = input.required<boolean>();
  public readonly close = output();
  private keycloak = inject(Keycloak);
  private router = inject(Router);
  readonly auth = inject(Auth);
  private exchangeRateAlertService = inject(ExchangeRateAlertService);
  readonly hasNoRateForToday = this.exchangeRateAlertService.hasNoRateForToday;

  constructor() {
    if (this.hasAccess([PERMISSIONS.comesaRatesManage])) {
      this.exchangeRateAlertService.checkTodayRate();
    }
  }

  // public allNavGroups: NavGroup[] = [ADMIN_NAV].map(group => this.filterGroup(group)).filter((group): group is NavGroup => group !== null);

  newNavGroups = SIDEBAR_NAV.map(group => this.filterGroup(group)).filter((group): group is NavGroup => group !== null);

  expandedItems = signal<Set<string>>(new Set(this.getInitialExpanded()));

  private currentUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(e => (e as NavigationEnd).urlAfterRedirects),
      startWith(this.router.url),
    ),
  );

  hasActiveChild(item: NavItem): boolean {
    const url = this.currentUrl() ?? '';
    return item.children?.some(child => child.route && url.startsWith(child.route)) ?? false;
  }

  private getInitialExpanded(): string[] {
    const url = this.router.url;
    const expanded: string[] = [];
    for (const group of SIDEBAR_NAV) {
      for (const item of group.children) {
        if (item.children?.some(child => child.route && url.startsWith(child.route))) {
          expanded.push(item.label);
        }
      }
    }
    return expanded;
  }

  toggleItem(label: string): void {
    this.expandedItems.update(set => {
      if (set.has(label)) return new Set<string>();
      return new Set([label]);
    });
  }

  collapseAll(): void {
    this.expandedItems.set(new Set());
  }

  isExpanded(label: string): boolean {
    return this.expandedItems().has(label);
  }

  dashboardItem: NavItem = {
    label: 'Dashboard',
    route: '/dashboard',
    icon: 'dashboard',
  };

  private itemKey(group: NavGroup, item: NavItem): string {
    return `${group.label}:${item.label}`;
  }

  private filterGroup(group: NavGroup): NavGroup | null {
    if (!this.hasAccess(group.requiredPermissions)) {
      return null;
    }

    const children = group.children
      .map(item => this.filterItem(item))
      .filter((item): item is NavItem => item !== null);

    if (!children.length) {
      return null;
    }

    return {...group, children};
  }

  private filterItem(item: NavItem): NavItem | null {
    if (!this.hasAccess(item.requiredPermissions)) {
      return null;
    }

    const children = item.children
      ?.map(child => this.filterItem(child))
      .filter((child): child is NavItem => child !== null);

    if (item.children) {
      if (!children?.length) {
        return null;
      }
      return {...item, children};
    }

    return item;
  }

  private hasAccess(permissions?: string[]): boolean {
    if (!permissions?.length) return true;
    return hasPermission(this.keycloak, ...permissions);
  }

  logout() {
    this.keycloak.logout({ redirectUri: window.location.origin });
  }
}
